import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import type { Location } from "@/content/locations";
import { FaqCardGrid } from "@/components/FaqCardGrid";

/** Content sections of a city hub, driven entirely by the city's `Location` record. */

export function CityHubWhy({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow="Why local matters"
      title={
        <>
          SEO built for <span className="text-gradient">{loc.city} buyers.</span>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-6">
        {loc.why.map((w, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <div className="flex gap-4 rounded-3xl border border-border p-7 bg-card">
              <span className="mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check size={16} />
              </span>
              <p className="text-muted-foreground leading-relaxed">{w}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      {items.map((item) => (
        <span key={item} className="rounded-full border border-border bg-card px-4 py-2 text-sm">
          {item}
        </span>
      ))}
    </div>
  );
}

export function CityHubCoverage({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow="Coverage"
      title={
        <>
          Industries & areas <span className="text-gradient">I work with in {loc.city}.</span>
        </>
      }
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <Reveal>
          <h3 className="font-display text-2xl">Industries served</h3>
          <Chips items={loc.industries} />
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="font-display text-2xl">Neighborhoods covered</h3>
          <Chips items={loc.neighborhoods} />
        </Reveal>
      </div>
    </Section>
  );
}

export function CityHubOpportunity({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow="The opportunity"
      title={
        <>
          What <span className="text-gradient">{loc.city} SEO</span> looks like today.
        </>
      }
    >
      <Reveal>
        <div className="rounded-3xl border border-border p-10 bg-card max-w-3xl">
          <p className="text-lg text-muted-foreground leading-relaxed">{loc.competitors}</p>
          <Link
            to="/case-studies"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            Review project examples & evidence notes{" "}
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </Reveal>
    </Section>
  );
}

export function CityHubResources({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow="Go deeper"
      title={
        <>
          Resources tailored for <span className="text-gradient">{loc.city} businesses.</span>
        </>
      }
    >
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loc.relatedLinks.map((rl, i) => (
          <Reveal key={rl.to + rl.label} delay={i * 0.04}>
            <Link
              to={rl.to as "/services"}
              className="group block h-full rounded-3xl border border-border p-7 bg-card hover:border-primary transition"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-display text-lg leading-snug">{rl.label}</h3>
                <ArrowUpRight
                  size={18}
                  className="text-muted-foreground group-hover:text-primary shrink-0"
                />
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{rl.blurb}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

function MapFact({ label, value, first }: { label: string; value: ReactNode; first?: boolean }) {
  return (
    <>
      <div
        className={`${first ? "" : "mt-5 "}text-xs uppercase tracking-[0.2em] text-muted-foreground`}
      >
        {label}
      </div>
      <div className="mt-2 font-display text-xl">{value}</div>
    </>
  );
}

export function CityHubMap({ loc }: { loc: Location }) {
  const { lat, lng } = loc.geo;
  const mapEmbedSrc = `https://www.google.com/maps?q=${lat},${lng}&z=12&output=embed`;
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  return (
    <Section
      eyebrow="On the map"
      title={
        <>
          Serving <span className="text-gradient">{loc.city}</span> & surrounding areas.
        </>
      }
    >
      <Reveal>
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden border border-border bg-card">
            <iframe
              title={`Map of ${loc.city}, ${loc.country}`}
              src={mapEmbedSrc}
              className="w-full aspect-[16/10] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="rounded-3xl border border-border p-8 bg-card">
            {loc.slug === "rangpur" && (
              <p className="mb-5 text-sm text-muted-foreground">
                This map shows the Rangpur service area, not an exact office pin.
              </p>
            )}
            <MapFact
              first
              label="Coordinates"
              value={
                <>
                  {lat}°N, {lng}°E
                </>
              }
            />
            <MapFact label="District" value={loc.district} />
            <MapFact label="Country" value={loc.country} />
            <a
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              Open in Google Maps <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}

export function CityHubFaq({ loc }: { loc: Location }) {
  return (
    <Section
      eyebrow={`${loc.city} SEO FAQ`}
      title={
        <>
          Common questions from <span className="text-gradient">{loc.city} businesses.</span>
        </>
      }
    >
      <FaqCardGrid items={loc.faq} />
    </Section>
  );
}
